import { useId, useState, type ReactNode } from 'react';
import { IconChart, IconTable } from '../Icons';

export interface TableSpec {
  columns: string[];
  rows: (string | number)[][];
}

interface Props {
  title: string;
  subtitle?: string;
  /** Rendered in the header, left of the chart/table toggle. */
  tools?: ReactNode;
  /**
   * The table view. Required, not optional: three of the light-mode series
   * colors sit below 3:1 contrast on white, and the palette's relief rule says
   * a chart carrying those must offer a table. Making it mandatory means no
   * chart can quietly skip it.
   */
  table: TableSpec;
  children: ReactNode;
  className?: string;
}

export function ChartFrame({ title, subtitle, tools, table, children, className }: Props) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const panelId = useId();

  return (
    <section className={`card ${className ?? ''}`}>
      <header className="card__head">
        <div className="card__titles">
          <h2 className="card__title">{title}</h2>
          {subtitle ? <p className="card__sub">{subtitle}</p> : null}
        </div>
        <div className="card__tools">
          {tools}
          <div className="segmented" role="group" aria-label={`${title} view`}>
            <button
              type="button"
              className="segmented__btn"
              aria-pressed={view === 'chart'}
              aria-controls={panelId}
              onClick={() => setView('chart')}
              title="Chart view"
            >
              <IconChart size={13} />
            </button>
            <button
              type="button"
              className="segmented__btn"
              aria-pressed={view === 'table'}
              aria-controls={panelId}
              onClick={() => setView('table')}
              title="Table view"
            >
              <IconTable size={13} />
            </button>
          </div>
        </div>
      </header>
      <div className="card__body" id={panelId}>
        {view === 'chart' ? (
          children
        ) : (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  {table.columns.map((c) => (
                    <th key={c} scope="col">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) =>
                      j === 0 ? (
                        <th key={j} scope="row" style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                          {cell}
                        </th>
                      ) : (
                        <td key={j}>{cell}</td>
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
